// <pos-onboarding-checklist/> — 首次登入三步驟引導（QA 付費條件 #1）
// see docs/QA_REPORT_20260707_minimax.md「開店第一天沒有 onboarding」
//
// 三步驟：① 建立菜單 → ② 設定收款 → ③ 開始點餐
// 完成偵測（皆走 RLS 同 tenant 查詢，fail-soft）：
//   ① menu_items 有任一筆
//   ② v_tenant_payment_credentials 有 active（或使用者按「先用現金」跳過 → localStorage）
//   ③ orders 有任一筆
// 全部完成或按「不再顯示」→ localStorage 記住，之後不再出現。

import { getSupabase } from '../lib/supabase-client.js';

const LS_HIDE = 'tauke.onboarding.hidden';
const LS_SKIP_PAY = 'tauke.onboarding.paySkipped';

class PosOnboardingChecklist extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.state = { loading: true, menuDone: false, payDone: false, orderDone: false };
  }

  connectedCallback() {
    if (localStorage.getItem(LS_HIDE) === '1') { this.remove(); return; }
    this.render();
    this.load();
  }

  async load() {
    try {
      const supabase = getSupabase();
      const [menu, pay, order] = await Promise.all([
        supabase.from('menu_items').select('id').limit(1),
        supabase.from('v_tenant_payment_credentials').select('id').eq('is_active', true).limit(1),
        supabase.from('orders').select('id').limit(1),
      ]);
      this.state.menuDone = !!menu.data?.length;
      this.state.payDone = !!pay.data?.length || localStorage.getItem(LS_SKIP_PAY) === '1';
      this.state.orderDone = !!order.data?.length;
    } catch { /* fail-soft：查詢失敗維持未完成顯示 */ }
    this.state.loading = false;

    // 三步全完成 → 慶祝一次後永久隱藏
    if (this.state.menuDone && this.state.payDone && this.state.orderDone) {
      localStorage.setItem(LS_HIDE, '1');
      this.remove();
      return;
    }
    this.render();
  }

  render() {
    const { loading, menuDone, payDone, orderDone } = this.state;
    const step = (done, num, title, desc, href, extra = '') => `
      <a class="step ${done ? 'is-done' : ''}" href="${href}">
        <span class="step__check">${done ? '✅' : num}</span>
        <span class="step__body">
          <span class="step__title">${title}</span>
          <span class="step__desc">${done ? '完成！' : desc}</span>
        </span>
        ${extra}
      </a>`;

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; font-family: var(--font-sans); }
        .card { background: var(--bg-elevated); border: 1px solid var(--brand-primary);
          border-radius: var(--r-lg); padding: var(--s-5); margin-bottom: var(--s-5); }
        h3 { margin: 0 0 var(--s-2); color: var(--brand-primary); font-size: 1.05rem;
          display: flex; justify-content: space-between; align-items: center; }
        .dismiss { background: none; border: 0; color: var(--fg-dim); cursor: pointer; font-size: .8rem; }
        .intro { color: var(--fg-muted); font-size: .85rem; margin: 0 0 var(--s-4); }
        .steps { display: grid; gap: var(--s-2); }
        .step { display: flex; align-items: center; gap: var(--s-3); padding: var(--s-3);
          border: 1px dashed var(--border); border-radius: var(--r-md);
          text-decoration: none; color: var(--fg-base); }
        .step:hover { background: var(--bg-surface); }
        .step.is-done { border-style: solid; opacity: .7; }
        .step__check { width: 28px; height: 28px; border-radius: 50%; background: var(--bg-surface);
          display: flex; align-items: center; justify-content: center; font-weight: 700;
          color: var(--brand-primary); flex-shrink: 0; }
        .step__body { display: flex; flex-direction: column; gap: 2px; flex: 1; }
        .step__title { font-weight: 600; }
        .step__desc { font-size: .8rem; color: var(--fg-muted); }
        .skip { font-size: .78rem; color: var(--fg-dim); background: none;
          border: 1px solid var(--border); border-radius: var(--r-full);
          padding: 4px 10px; cursor: pointer; flex-shrink: 0; }
      </style>
      <div class="card">
        <h3>🚀 三步驟開始做生意
          <button class="dismiss" data-act="hide">不再顯示 ✕</button>
        </h3>
        <p class="intro">照著做完，今天就能收第一筆錢。${loading ? '（檢查進度中…）' : ''}</p>
        <div class="steps">
          ${step(menuDone, '①', '建立菜單', '把你賣的品項加進來（先加 3-5 樣就好）', '#/menu')}
          ${step(payDone, '②', '設定收款', '接綠界或 LINE Pay 線上收款', '#/settings/payment',
            payDone ? '' : '<button class="skip" data-act="skip-pay">先用現金收款</button>')}
          ${step(orderDone, '③', '開始點餐', '到點餐頁完成第一筆訂單', '#/pos')}
        </div>
      </div>
    `;

    this.shadowRoot.querySelector('[data-act="hide"]')?.addEventListener('click', (e) => {
      e.preventDefault();
      localStorage.setItem(LS_HIDE, '1');
      this.remove();
    });
    this.shadowRoot.querySelector('[data-act="skip-pay"]')?.addEventListener('click', (e) => {
      e.preventDefault(); e.stopPropagation();
      localStorage.setItem(LS_SKIP_PAY, '1');
      this.state.payDone = true;
      if (this.state.menuDone && this.state.orderDone) {
        localStorage.setItem(LS_HIDE, '1'); this.remove(); return;
      }
      this.render();
    });
  }
}

customElements.define('pos-onboarding-checklist', PosOnboardingChecklist);
